import { useSelector, useStore } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useEffect, useRef } from 'react'

import landingZonesApi from '../../api/landingZones'

import * as landingZonesMapping from '../../store/actions/landingZones'
import * as landingZonesMappingType from '../../store/actions/landingZones/types'

import { useAppLoader } from '@services/hooks/AppLoader'
import { useApiError } from '@services/hooks/ApiError'

export const AZURE = 'azure'
export const AWS = 'aws'

export const CONTEXT = { [AWS]: 'AWS', [AZURE]: 'IAS' }

export const CONNECTED = 'connected'
export const DISCONNECTED = 'disconnected'

export const REGIONS_STR = {
    frc: 'France central',
    eun: 'North Europe',
    euw: 'West Europe',
    swc: 'Sweden central',
    'eu-central-1': 'EU central 1',
    'eu-west-1': 'EU west 1',
    'us-east-1': 'US east 1'
}

export const useLandingZones = () => {
    const store = useStore()
    const dataStore = useSelector(state => state.landingZones)
    const landingZonesAction = bindActionCreators(landingZonesMapping, store.dispatch)

    const { hide: hideLoader, show: showLoader } = useAppLoader()
    const { handleError: apiError } = useApiError()

    const loaderCallback = useRef(null)
    const displayLoader = useRef(false)

    useEffect(() => {
        const azureConnected = store.getState().landingZones.loaded.azure.connected && store.getState().landingZones.loaded.azure.disconnected
        const awsConnected = store.getState().landingZones.loaded.aws.connected

        if (azureConnected && awsConnected) format()
    }, [store.getState().landingZones.loaded.azure, store.getState().landingZones.loaded.aws])

    const getData = (index = null) => {
        if (!index) return store.getState().landingZones
        return store.getState().landingZones[index]
    }

    const getRobotData = async (id, context, config) => {
        const { endpoint, apiLoader, queryParams = {} } = config

        let allItems = []
        let skipToken = null

        const fetchPage = (token) => {
            return new Promise((resolve, reject) => {
                const params = new URLSearchParams({
                    context,
                    landingZoneId: id,
                    expand: 'true',
                    pageSize: '100',
                    ...queryParams,
                    ...(token && { skipToken: token })
                })

                const url = `${endpoint}?${params}`

                apiLoader.load(url, AZURE, 'robot', '/user_impersonation', {'x-api-version': '4.0'}, {
                    success: (result) => { resolve(result?.data) },
                    error: (result) => { reject(result.data.message) }
                })
            })
        }

        do {
            const pageData = await fetchPage(skipToken)
            allItems = [...allItems, ...pageData.pageItems]
            skipToken = pageData.skipToken || null
        } while (skipToken !== null)

        return allItems
    }

    const getAzureDisconnected = () => {
        landingZonesApi.load('/api/landing-zones/disconnected', AZURE, 'patrol', '/user_impersonation', {'x-api-version': '2.0'}, {
            success: (result) => {
                landingZonesAction.populate(result?.data?.data ?? [], landingZonesMappingType.POPULATE_DISCONNECTED, landingZonesMappingType.POPULATE_AZURE, () => {
                    landingZonesAction.setLoaded(AZURE, DISCONNECTED)
                })
            },
            error: (result) => { apiError(result.data.message) }
        })
    }

    const getAzureConnected = () => {
        landingZonesApi.load('/api/landing-zones', AZURE, 'lzManager', '/user_impersonation', {'x-api-version': '1.0'}, {
            success: (result) => {
                landingZonesAction.populate(result?.data?.data ?? [], landingZonesMappingType.POPULATE_CONNECTED, landingZonesMappingType.POPULATE_AZURE, () => {
                    landingZonesAction.setLoaded(AZURE, CONNECTED)
                })
            },
            error: (result) => { apiError(result.data.message) }
        })
    }

    const getAwsConnected = () => {
        landingZonesApi.load('/landing-zones', AWS, 'lzManager', null, {}, {
            success: (result) => {
                landingZonesAction.populate(result?.data?.landingzones ?? [], landingZonesMappingType.POPULATE_CONNECTED, landingZonesMappingType.POPULATE_AWS, () => {
                    landingZonesAction.setLoaded(AWS, CONNECTED)
                })
            },
            error: (result) => { apiError(result.data.message) }
        })
    }

    const inLandingZones = (id, isNotAws = true) => {
        const lzDataStore = store.getState().landingZones

        for (let i = 0; i < lzDataStore.formated.list.length; i++) {
            const lz = lzDataStore.formated.list[i]
            if (lz.id === id) {
                if (isNotAws && lz.provider === AWS) continue
                return true
            }
        }

        return false
    }

    const getProvider = (id) => {
        const lzDataStore = store.getState().landingZones

        for (let i = 0; i < lzDataStore.formated.list.length; i++) {
            const lz = lzDataStore.formated.list[i]
            if (lz.id === id) return lz.provider
        }

        return null
    }

    const isDisconnected = (id) => {
        for (let i = 0; i < dataStore.data.azure.disconnected.length; i++)
            return (dataStore.data.azure.disconnected[i] === id)
    }

    const format = () => {
        const result = []
        let mode = 0

        for (let i = 0; i < dataStore.data.azure.connected.length; i++) {
            if (mode === 0) mode = 1
            const lz = dataStore.data.azure.connected[i]
            result.push({
                id: lz.lzId,
                label: lz.lzId,
                subscription: lz.subscriptionName,
                mode: (isDisconnected()) ? DISCONNECTED : CONNECTED,
                provider: AZURE,
                memberships: lz.memberships
            })
        }

        for (let i = 0; i < dataStore.data.aws.connected.length; i++) {
            if (mode === 0) mode = 2
            if (mode === 1) mode = 3
            const lz = dataStore.data.aws.connected[i]
            result.push({
                id: lz.accountId,
                label: lz.accountId,
                subscription: lz.accountId,
                mode: CONNECTED,
                provider: AWS,
                memberships: []
            })
        }

        landingZonesAction.setFormat(result, landingZonesMappingType.SET_FORMAT, landingZonesMappingType.SET_FORMAT_LIST, () => {
            landingZonesAction.setFormat(mode, landingZonesMappingType.SET_FORMAT, landingZonesMappingType.SET_FORMAT_MODE, () => {
                if (loaderCallback.current) return loaderCallback.current()
                if (displayLoader.current) hideLoader()
            })
        })
    }

    const getFilterList = (provider = null) => {
        provider = provider?.toLowerCase() ?? null
        const data = getData('formated') ?? {}

        const list = data.list ?? []
        const result = []

        if (provider) {
            result.push({id: 'separator', label: provider.toUpperCase()})
            for (let i = 0; i < list.length; i++) {
                if (list[i].provider === provider)
                    result.push({id: list[i].id, label: list[i].label})
            }
        }
        else {
            let providers = []
            if (dataStore.formated.mode === 1) providers = [AZURE]
            else if (dataStore.formated.mode === 2) providers = [AWS]
            else if (dataStore.formated.mode === 3) providers = [AZURE, AWS]

            for (let i = 0; i < providers.length; i++) {
                for (let j = 0; j < list.length; j++) {
                    if (j === 0) result.push({id: 'separator', label: providers[i].toUpperCase()})
                    if (list[j].provider === providers[i])
                        result.push({id: list[j].id, label: list[j].label})
                }
            }
        }

        return result
    }

    const load = (callback = null, loader = true) => {
        loaderCallback.current = callback
        displayLoader.current = loader

        if (loader) showLoader(` `)

        getAzureConnected()
        getAzureDisconnected()
        getAwsConnected()
    }

    return { load, inLandingZones, getProvider, getFilterList, getRobotData }
}